'use client';

import { useMemo } from 'react';

import { useAuthUser } from '@/hooks/useAuthUser';
import { toggleDevMode, useDevMode } from '@/hooks/useDevMode';
import { useSearchParams } from 'next/navigation';

const DEV_ALLOWLIST_EMAILS: string[] = (
  process.env['NEXT_PUBLIC_DEV_ALLOWLIST_EMAILS'] ?? ''
)
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

/**
 * Floating toggle button to switch dev mode on/off.
 * Visible to allowlisted users, local dev, or when ?dev=true is present.
 */
export default function DevToggle() {
  const isDevMode = useDevMode();
  const { userEmail } = useAuthUser();
  const searchParams = useSearchParams();

  const isVisible = useMemo(() => {
    const isDev = process.env.NODE_ENV === 'development';
    const isAllowlisted =
      !!userEmail && DEV_ALLOWLIST_EMAILS.includes(userEmail.toLowerCase());
    const hasDevParam = searchParams.get('dev') === 'true';
    return isDev || isAllowlisted || hasDevParam;
  }, [searchParams, userEmail]);

  if (!isVisible) return null;

  return (
    <button
      onClick={() => toggleDevMode()}
      title={
        isDevMode
          ? 'Dev mode ON - click to disable'
          : 'Dev mode OFF - click to enable'
      }
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg border transition-all duration-200 ${
        isDevMode
          ? 'bg-status-info text-white border-status-info shadow-status-info/30'
          : 'bg-surface-card text-content-secondary border-line-default hover:border-line-strong'
      }`}
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full transition-colors ${
          isDevMode ? 'bg-white' : 'bg-content-muted'
        }`}
      />
      DEV
    </button>
  );
}
